import { Injectable, OnDestroy, OnInit } from "@angular/core";
import { Observable, Subject, Subscription } from "rxjs";
import { LoggingService } from "../../services/logging.service";
import { Toro } from "../common/enumerations";
import { ChangeInSceneForceStatusNotification } from "../models/notifications/change-in-scene-force-status-notification.model";
import { MagnetoScenePlayNotification } from "../models/notifications/magneto-scene-play-notification.model";

import { Store } from '@ngrx/store';
import { AddMagnetoNotification } from "../models/notifications/add-magneto-report-notification.model";
import { ChangeInThemeForceStatusNotification } from "../models/notifications/change-in-theme-force-status-notification.model";
import { ControllerAllOffNotificationModel } from "../models/notifications/controller-all-off-notification.model";
import { ControllerRecordChangeNotificationModel } from "../models/notifications/controller-record-change-notification.model";
import { ControllerStatusChangedNotificationModel } from "../models/notifications/controller-status-changed-notification.model";
import { DeviceBusyNotification } from "../models/notifications/device-busy-notification.model";
import { EvoAddReplaceProcessUpdateNotification } from "../models/notifications/evo-add-replace-process-update-notification.model";
import { FirmwareUpgradeProgressNotification } from "../models/notifications/firmware-upgrade-progress-notification.model";
import { IrrigationControllerResyncRecordsNotification } from "../models/notifications/irrigation-controller-resync-records-notification.model";
import { LpcuStatusUpdatedNotification } from "../models/notifications/lpcu-status-updated-notification.model";
import { MagnetoDiscoveryNotification } from "../models/notifications/magneto-discovery-notification.model";
import { MailCenterNotification } from "../models/notifications/mail-center-notification.model";
import { NewDeviceDiscoveredNotification } from "../models/notifications/new-device-discovered-notification.model";
import { ProgramManualStartNotificationModel } from "../models/notifications/program-manual-start-notification.model";
import { RunningModeChangeAcceptedNotificationModel } from "../models/notifications/runnning-mode-change-accepted-notification.model";
import { StationManualStartNotificationModel } from "../models/notifications/station-manual-start-notification.model";
import { UlReportSetConfigNotification } from "../models/notifications/ul-report-set-config-notification.model";
import { ToroNotification } from "../models/toro-notification.model";
import { NotificationService } from "../services/notification.service";
import { ToroLocationState } from './../../accounts/store/toro-location/toro-location.entity';
import { getCurrentLocationDetail } from "./../../accounts/store/toro-location/toro-location.selectors";
import { MagnetoFixturePreviewNotification } from "./../models/notifications/magneto-fixture-preview-notification.model";
import { MagnetoFixtureThemeSaveNotification } from "./../models/notifications/magneto-fixture-theme-save-notification.model";
import { MagnetoGetConfigNotification } from "./../models/notifications/magneto-get-config-notification.model";
import { MagnetoRecordChangeNotification } from "./../models/notifications/magneto-record-change-notification.model";
import { MagnetoSceneChangeNotification } from "./../models/notifications/magneto-scene-change-notification.model";
import { MagnetoStatusUpdatedNotification } from "./../models/notifications/magneto-status-updated-notification.model";
import { MagnetoSyncSetConfigR1Notification } from "./../models/notifications/magneto-sync-set-config-r1-notification.model";
import { MagnetoSyncSetConfigR2Notification } from "./../models/notifications/magneto-sync-set-config-r2-notification.model";
import { MagnetoSyncStatusCommandErrorNotification } from "./../models/notifications/magneto-sync-status-command-error-notification.model";
import { MagnetoSyncStatusMatchedNotification } from "./../models/notifications/magneto-sync-status-matched-notification.model";
import { MagnetoThemeChangeNotification } from "./../models/notifications/magneto-theme-change-notification.model";
import { MagnetoThemePlayNotification } from "./../models/notifications/magneto-theme-play-notification.model";
import { ToroLocationDetailed } from './../models/toro-location-detailed.model';
import { LocalCacheManager } from "./local-cache.manager";
import { LxcAddReportNotification } from "../models/notifications/lxc-add-report-notification.model";
import { Constants } from "../../core/constants";
import { ConfigsAreDifferentNotification } from "../models/notifications/configs-are-different-notification";

@Injectable()
export class NotificationManagerService implements OnDestroy, OnInit {
    // Subjects

    isGatewayOnline = new Subject<any>();

    changeInSceneForceStatusNotificationReceived =
        new Subject<ChangeInSceneForceStatusNotification>();

    // Magneto Subject

    changeInThemeForceStatusNotificationReceived =
        new Subject<ChangeInThemeForceStatusNotification>();
    deviceBusyNotification = new Subject<DeviceBusyNotification>();
    deviceDiscoveryReportReceived = new Subject();
    evoAddReplaceProcessUpdateNotificationReceived =
        new Subject<EvoAddReplaceProcessUpdateNotification>();
    firmwareUpgradeProgressNotificationReceived =
        new Subject<FirmwareUpgradeProgressNotification>();
    configsAreDifferentNotificationReceived =
        new Subject<ConfigsAreDifferentNotification>();
    irrigationControllerStationOnOff =
        new Subject<StationManualStartNotificationModel>();
    irrigationControllerProgramStartStop =
        new Subject<ProgramManualStartNotificationModel>();
    irrigationControllerAllOff =
        new Subject<ControllerAllOffNotificationModel>();
    irrigationControllerStatusChanged =
        new Subject<ControllerStatusChangedNotificationModel>();
    irrigationControllerRecordChange =
        new Subject<ControllerRecordChangeNotificationModel>();
    irrigationControllerRecordReSyncNotification =
        new Subject<IrrigationControllerResyncRecordsNotification>();
    lpcuStatusIsUpdatedNotificationReceived =
        new Subject<LpcuStatusUpdatedNotification>();
    mailCenterNotificationReceived = new Subject<MailCenterNotification>();
    newDeviceDiscoveredNotification = new Subject<any>();
    pollSubscription: Subscription;
    programStoppedNotification = new Subject<any>();
    runningModeChangeAccepted =
        new Subject<RunningModeChangeAcceptedNotificationModel>();
    testAllStationsNotification = new Subject<any>();
    uLReportSetConfigNotification =
        new Subject<UlReportSetConfigNotification>();

    //notifications for Magneto device
    magnetoGetConfigNotification = new Subject<MagnetoGetConfigNotification>();
    magnetoDiscoveryNotification = new Subject<MagnetoDiscoveryNotification>();
    magnetoRecordChangeNotification =
        new Subject<MagnetoRecordChangeNotification>();
    addMagnetoNotification = new Subject<AddMagnetoNotification>();

    magnetoSceneChangeNotification =
        new Subject<MagnetoSceneChangeNotification>();
    magnetoThemeChangeNotification =
        new Subject<MagnetoThemeChangeNotification>();
    magnetoFixtureThemeSaveNotification =
        new Subject<MagnetoFixtureThemeSaveNotification>();
    magnetoFixturePreviewNotification =
        new Subject<MagnetoFixturePreviewNotification>();
    magnetoSyncSetConfigR1Notification =
        new Subject<MagnetoSyncSetConfigR1Notification>();
    magnetoSyncSetConfigR2Notification =
        new Subject<MagnetoSyncSetConfigR2Notification>();
    magnetoSyncStatusCommandErrorNotification =
        new Subject<MagnetoSyncStatusCommandErrorNotification>();
    magnetoSyncStatusMatchedNotification =
        new Subject<MagnetoSyncStatusMatchedNotification>();

    magnetoScenePlayNotification = new Subject<MagnetoScenePlayNotification>();
    magnetoThemePlayNotification = new Subject<MagnetoThemePlayNotification>();
    magnetoStatusIsUpdatedNotification =
        new Subject<MagnetoStatusUpdatedNotification>();

    deleteMagnetoNotification = new Subject<any>();

    // SignalR connection status
    signalRConnectionStatusCheckNotification = new Subject<boolean>();
    notificationReceivedOverSignalR = new Subject<ToroNotification>();
    isPolling = false;
    lastNotificationId: string;
    lastMessageIdIfResultOfUserRequest: string;
    currentAccountId: number;
    currentLocation: ToroLocationDetailed;
    // TODO MagnetoGetConfigNotification

    locationSubscription: Subscription;
    signalRNotificationSubscription: Subscription;
    timerRef;
    oldInterval: number;
    //LXC Notification
    lxcAddReportNotificationReceived = new Subject<LxcAddReportNotification>();

    constructor(
        private notificationService: NotificationService,
        private localCacheManager: LocalCacheManager,
        private toroLocationStore: Store<ToroLocationState>
    ) {
        this.lastNotificationId =
            this.localCacheManager.getLastNotificationId(this.currentAccountId);
        this.locationSubscription = this.toroLocationStore.select(getCurrentLocationDetail).subscribe(loc => {
            if (loc && loc !== null) {
                this.currentLocation = loc;
                this.currentAccountId = this.currentLocation.AccountId;
            } else {
                this.currentLocation = null;
            }
        });

        this.signalRNotificationSubscription = this.notificationReceivedOverSignalR.subscribe(notification => {
            // notification.Id = notification.Id === undefined ? notification.NotificationId : notification.Id;

            //console.log('LN signalRNotificationSubscription  ',notification);
            if (this.currentAccountId && this.currentAccountId === notification.AccountId) {
                    if (this.lastNotificationId !== notification.Id) {
                        this.processNotification(notification);
                        this.lastNotificationId = notification.Id;
                        this.localCacheManager.saveLastNotificationId(
                            this.currentAccountId,
                            this.lastNotificationId
                            );
                        }
                }
        })
    }

    ngOnInit() {
    }

    ngOnDestroy() {
        if (this.pollSubscription) {
            this.pollSubscription.unsubscribe();
        }
        if (this.locationSubscription) {
            this.locationSubscription.unsubscribe();
        }

        if (this.signalRNotificationSubscription) {
            this.signalRNotificationSubscription.unsubscribe();
        }

        this.clearIntervalPolling();
    }

    public startPollingByInterval(accountId: number, interval) {
        this.currentAccountId = accountId;
        this.lastNotificationId =
            this.localCacheManager.getLastNotificationId(accountId);
        this.lastMessageIdIfResultOfUserRequest = this.localCacheManager.getLastMessageIdIfResultOfUserRequest(accountId);
        if (!this.isPolling) {
            this.isPolling = true;
            this.pollForNotifications(interval);
        }
        if (this.isPolling && interval == this.oldInterval) {
            return;
        }
        if (interval && this.timerRef) {
            this.clearIntervalPolling();
            this.oldInterval = interval;
        }
        this.timerRef = setInterval(() => {
            this.pollForNotifications(interval);
        }, interval);
        return this.timerRef;
    }

    clearIntervalPolling() {
        clearInterval(this.timerRef);
        this.timerRef = undefined;
    }

    public startPolling(accountId: number) {
        this.currentAccountId = accountId;
        this.lastNotificationId =
            this.localCacheManager.getLastNotificationId(accountId);
        this.lastMessageIdIfResultOfUserRequest = this.localCacheManager.getLastMessageIdIfResultOfUserRequest(accountId);
        if (this.isPolling) {
            return;
        }

        this.isPolling = true;
        this.pollForNotifications();
    }

    public stopPolling() {
        this.isPolling = false;

        if (this.pollSubscription) {
            this.pollSubscription.unsubscribe();
        }
    }

    public pollForNotifications(interval = Constants.longPollInterval) {
        if (!this.isPolling || !this.currentAccountId) {
            return;
        }
        this.pollSubscription = this.notificationService
            .pollForNotifications(
                this.currentAccountId,
                this.lastNotificationId
            )
            .subscribe(
                (notifications: ToroNotification[]) => {
                    if (notifications.length > 0) {
                        // notifications = notifications.filter(x => {
                        //     let endTime = new Date();
                        //     let startTime = new Date();
                        //     startTime.setMinutes(startTime.getMinutes() - 1);
                        //     let tmst = new Date(x.TimeStamp);
                        //     return tmst <= endTime && tmst >= startTime;

                        // })

                        let endTime = new Date();
                        let startTime = new Date();
                        startTime.setSeconds(startTime.getSeconds() - 20);

                        notifications = notifications.filter(x => x.TimeStamp <= endTime && x.TimeStamp >= startTime);

                        notifications.forEach((notification) => {
                            if (this.lastNotificationId !== notification.Id) {
                                this.processNotification(notification);
                                this.lastNotificationId = notification["Id"];
                            }
                        });

                        this.localCacheManager.saveLastNotificationId(
                            this.currentAccountId,
                            this.lastNotificationId
                        );
                    }

                    //this.pollForNotifications();
                },
                (error) => {
                    this.restartPollingAfterDelay();
                }
            );
    }

    public restartPolling(accountId, interval) {
        if (!this.isPolling) {
            //this.startPolling(accountId);
            this.startPollingByInterval(accountId, interval);
        }
    }

    private restartPollingAfterDelay() {
        setTimeout(() => {
            this.pollForNotifications();
        }, Constants.longPollInterval);
    }

    private processNotification(notification: ToroNotification) {
        if (
            notification.ValueType !==
            Toro.NotificationType.MailCenterNotification
        ) {
            //LoggingService.logNotification(notification);
        }
        this.lastMessageIdIfResultOfUserRequest = this.localCacheManager.getLastMessageIdIfResultOfUserRequest(this.currentAccountId);
        // this.lastNotificationId = notification["Id"];

        // For debugging
        // LoggingService.log(`Notification Id: ${this.lastNotificationId}`);
        //console.log('LN notification  ',notification);

        switch (notification.ValueType) {
            case Toro.NotificationType.ChangeInSceneForceStatusNotification:
                this.changeInSceneForceStatusNotificationReceived.next(
                    new ChangeInSceneForceStatusNotification(notification.Value)
                );
                break;

            case Toro.NotificationType.MagnetoScenePlayNotification:
                this.magnetoScenePlayNotification.next(
                    new MagnetoScenePlayNotification(notification.Value)
                );
                break;
            case Toro.NotificationType.MagnetoThemePlayNotification:
                this.magnetoThemePlayNotification.next(
                    new MagnetoThemePlayNotification(notification.Value)
                );
                break;
            case Toro.NotificationType.ChangeInThemeForceStatusNotification:
                this.changeInThemeForceStatusNotificationReceived.next(
                    new ChangeInThemeForceStatusNotification(notification.Value)
                );
                break;
            case Toro.NotificationType.EvoAddReplaceProcessUpdateNotification:
                //Cant filter this notification as we dont have location Id in start
                this.evoAddReplaceProcessUpdateNotificationReceived.next(
                    new EvoAddReplaceProcessUpdateNotification(
                        notification.Value,
                        notification.Id
                    )
                );
                break;

                case Toro.NotificationType.FirmwareUpgradeProgressNotification:
                    if (this.currentLocation && this.currentLocation.Id === notification.LocationId) {
                        this.firmwareUpgradeProgressNotificationReceived.next(
                            new FirmwareUpgradeProgressNotification(notification.Value)
                        );
                    }
                    break;

                case Toro.NotificationType.ConfigsAreDifferentNotification:
                    if (this.currentLocation && this.currentLocation.Id === notification.LocationId) {
                        this.configsAreDifferentNotificationReceived.next(
                            new ConfigsAreDifferentNotification(notification.Value)
                        );
                    }
                    break;

            case Toro.NotificationType.IncomingReportFailedNotification:
                if (notification.Value.ReportName === "CL_DSC") {
                    this.deviceDiscoveryReportReceived.next();
                } else {
                    //console.log(notification);
                }
                break;

            case Toro.NotificationType.IrrigationControllerAllOff:
                if (this.currentLocation && this.currentLocation.Id === notification.LocationId) {
                    this.irrigationControllerAllOff.next(
                    new ControllerAllOffNotificationModel(notification.Value)
                    );
                }
                break;

            case Toro.NotificationType.IrrigationControllerRecordChange:
                if (this.currentLocation && this.currentLocation.Id === notification.LocationId) {
                    this.irrigationControllerRecordChange.next(
                        new ControllerRecordChangeNotificationModel(
                            notification.Value
                        )
                    );
                }
                break;

            case Toro.NotificationType.IrrigationControllerRecordReSync:
                if (this.currentLocation && this.currentLocation.Id === notification.LocationId) {
                    this.irrigationControllerRecordReSyncNotification.next(
                    new IrrigationControllerResyncRecordsNotification( notification.Value ) );
                }
                break;

            case Toro.NotificationType.IrrigationControllerStationOnOff:
                if (this.currentLocation && this.currentLocation.Id === notification.LocationId) {
                    this.irrigationControllerStationOnOff.next(
                        new StationManualStartNotificationModel(notification.Value)
                    );
                }
            break;

            case Toro.NotificationType.IrrigationControllerStatusChanged:
                if (this.currentLocation && this.currentLocation.Id === notification.LocationId) {
                    this.irrigationControllerStatusChanged.next(
                        new ControllerStatusChangedNotificationModel(
                            notification.Value
                        )
                    );
                }
                break;

            case Toro.NotificationType.IrrigationControllerProgramStartStop:
                if (this.currentLocation && this.currentLocation.Id === notification.LocationId) {
                    this.irrigationControllerProgramStartStop.next(
                        new ProgramManualStartNotificationModel(notification.Value)
                    );
                }
                break;

            case Toro.NotificationType.GatewayNewDeviceDiscoveredNotification:
                this.newDeviceDiscoveredNotification.next(
                    new NewDeviceDiscoveredNotification(notification.Value)
                );
                break;

            case Toro.NotificationType.LPCUStatusIsUpdatedNotification:
                this.lpcuStatusIsUpdatedNotificationReceived.next(
                    new LpcuStatusUpdatedNotification(notification.Value)
                );
                break;

            case Toro.NotificationType.MagnetoStatusIsUpdatedNotification:
                this.magnetoStatusIsUpdatedNotification.next(
                    new MagnetoStatusUpdatedNotification(notification.Value)
                );
                break;

            case Toro.NotificationType.MailCenterNotification:
                if (this.currentLocation && this.currentLocation.Id === notification.LocationId) {
                    this.mailCenterNotificationReceived.next(
                        new MailCenterNotification(notification.Value));
                }
                break;

            case Toro.NotificationType.RunningModeChangeAccepted:
                this.runningModeChangeAccepted.next(
                    new RunningModeChangeAcceptedNotificationModel(
                        notification.Value
                    )
                );
                break;

            case Toro.NotificationType.DeviceBusyNotification:
                this.deviceBusyNotification.next(
                    new DeviceBusyNotification(notification.Value)
                );
                break;

            case Toro.NotificationType.ULReportSetConfigNotification:
                this.uLReportSetConfigNotification.next(
                    new UlReportSetConfigNotification(notification.Value)
                );
                break;

            case Toro.NotificationType.AddMagnetoNotification:

                let addMagnetoNotification = new AddMagnetoNotification(notification.Value);

                if (addMagnetoNotification.MessageIdIfResultOfUserRequest !== this.lastMessageIdIfResultOfUserRequest) {
                    this.localCacheManager.saveLastMessageIdIfResultOfUserRequest(
                        this.currentAccountId,
                        addMagnetoNotification.MessageIdIfResultOfUserRequest

                    );
                    this.addMagnetoNotification.next(addMagnetoNotification);
                }
                break;

            case Toro.NotificationType.MagnetoGetConfigNotification:

                let magnetoConfigNotification = new MagnetoGetConfigNotification(notification.Value);

                if (magnetoConfigNotification.MessageIdIfResultOfUserRequest !== this.lastMessageIdIfResultOfUserRequest) {
                    this.localCacheManager.saveLastMessageIdIfResultOfUserRequest(
                        this.currentAccountId,
                        magnetoConfigNotification.MessageIdIfResultOfUserRequest
                    );
                    this.magnetoGetConfigNotification.next(magnetoConfigNotification);
                }
                break;

            case Toro.NotificationType.MagnetoDiscoveryNotification:
                this.magnetoDiscoveryNotification.next(
                    new MagnetoDiscoveryNotification(notification.Value)
                );
                break;

            case Toro.NotificationType.MagnetoRecordChangeNotification:
                this.magnetoRecordChangeNotification.next(
                    new MagnetoRecordChangeNotification(notification.Value)
                );
                break;

            case Toro.NotificationType.MagnetoSceneChangeNotification:
                this.magnetoSceneChangeNotification.next(
                    new MagnetoSceneChangeNotification(notification.Value)
                );
                break;

            case Toro.NotificationType.MagnetoThemeChangeNotification:
                this.magnetoThemeChangeNotification.next(
                    new MagnetoThemeChangeNotification(notification.Value)
                );
                break;

            case Toro.NotificationType.MagnetoFixtureThemeSaveNotification:
                this.magnetoFixtureThemeSaveNotification.next(
                    new MagnetoFixtureThemeSaveNotification(notification.Value)
                );
                break;

            case Toro.NotificationType.MagnetoFixturePreviewNotification:
                this.magnetoFixturePreviewNotification.next(
                    new MagnetoFixturePreviewNotification(notification.Value)
                );
                break;

            case Toro.NotificationType.MagnetoSyncSetConfigR1Notification:
                this.magnetoSyncSetConfigR1Notification.next(
                    new MagnetoSyncSetConfigR1Notification(notification.Value)
                );
                break;

            case Toro.NotificationType.MagnetoSyncSetConfigR2Notification:
                this.magnetoSyncSetConfigR2Notification.next(
                    new MagnetoSyncSetConfigR2Notification(notification.Value)
                );
                break;

            case Toro.NotificationType.MagnetoSyncStatusCommandErrorNotification:
                this.magnetoSyncStatusCommandErrorNotification.next(
                    new MagnetoSyncStatusCommandErrorNotification(
                        notification.Value
                    )
                );
                break;

            case Toro.NotificationType.MagnetoSyncStatusMatchedNotification:
                this.magnetoSyncStatusMatchedNotification.next(
                    new MagnetoSyncStatusMatchedNotification(
                        notification.Value
                    )
                );
                break;

            case Toro.NotificationType.LxcAddReportNotification:
                if (this.currentLocation && this.currentLocation.Id === notification.LocationId) {
                    this.lxcAddReportNotificationReceived.next(
                        new LxcAddReportNotification(
                            notification.Value
                        )
                    );
                }
                break;

            default:
                LoggingService.log(
                    `>>> UNHANDLED NOTIFICATION: ${notification.ValueType} <<<`
                );
                break;
        }
    }

    sendDeleteMagnetoNotification() {
        this.deleteMagnetoNotification.next(true);
    }

    getDeleteMagnetoNotification(): Observable<any> {
        return this.deleteMagnetoNotification.asObservable();
    }
}
